import type { BetslipTicket } from '@/domain/betslip';
import type { OpenCashoutTicket } from '@/domain/cashout';
import type { PlaceBetRequest, PlaceBetResponse } from '@/domain/place-bet';
import type {
  TicketCountRequest,
  TicketCountResponse,
  TicketListItem,
  TicketListRequest,
  TicketListResponse,
} from '@/domain/tickets';
import { api } from './client';
import { endpoints } from './endpoints';

/** UsersAPI `TicketCashoutRequest` for `sb/api/tickets/cashoutnew`. */
export interface CashoutTicketRequest {
  Id: string;
  User: string;
  CashoutValue: number;
  Options?: string;
  PartialCashoutPercentage?: number;
  TotalCashoutStake?: number;
  Code?: string;
}

export interface CashoutLockedEvent {
  EventId?: number;
  Market?: string | null;
  Outcome?: string | null;
  Specifier?: string | null;
  Status?: number;
  Limit?: number;
}

/** UsersAPI `TicketCashoutResponse`. */
export interface CashoutTicketResponse {
  Success: boolean;
  Error?: string | null;
  Id?: string;
  Status?: number;
  Amount?: number;
  Changed?: boolean;
  ChangedAmount?: number | null;
  LockedEvents?: CashoutLockedEvent[] | null;
}

/**
 * Place a funded ticket via Users API (`sb/api/tickets/create`).
 * Body is form-encoded (users host default) so nested `Items` bind correctly.
 */
export function createTicket(
  ticket: PlaceBetRequest,
  signal?: AbortSignal,
): Promise<PlaceBetResponse> {
  return api.post<PlaceBetResponse>(
    endpoints.tickets.create.host,
    endpoints.tickets.create.path,
    ticket as unknown as Record<string, unknown>,
    signal,
  );
}

/**
 * Place a KreditBet via Users API (`sb/api/creditbet/create`).
 * Stake is covered by an open creditor ticket's possible win (no wallet debit at place).
 */
export function createCreditBet(
  ticket: PlaceBetRequest,
  signal?: AbortSignal,
): Promise<PlaceBetResponse> {
  return api.post<PlaceBetResponse>(
    endpoints.tickets.creditBet.host,
    endpoints.tickets.creditBet.path,
    ticket as unknown as Record<string, unknown>,
    signal,
  );
}

/**
 * Eligible open tickets for KreditBet (`sb/api/tickets/creditorlist`).
 * Body is the current betslip-shaped ticket (events used for overlap filter).
 */
export function listCreditorTickets(
  ticket: BetslipTicket | PlaceBetRequest,
  signal?: AbortSignal,
): Promise<TicketListItem[]> {
  return api.post<TicketListItem[]>(
    endpoints.tickets.creditorList.host,
    endpoints.tickets.creditorList.path,
    ticket as unknown as Record<string, unknown>,
    signal,
  );
}

/** Open / settled / saved ticket list (`sb/api/tickets/list`). */
export function listTickets(
  request: TicketListRequest,
  signal?: AbortSignal,
): Promise<TicketListResponse> {
  return api.post<TicketListResponse>(
    endpoints.tickets.list.host,
    endpoints.tickets.list.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

/** Open + live badge counts (`sb/api/tickets/count`). */
export function countTickets(
  request: TicketCountRequest,
  signal?: AbortSignal,
): Promise<TicketCountResponse> {
  return api.post<TicketCountResponse>(
    endpoints.tickets.count.host,
    endpoints.tickets.count.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}

/**
 * Rebuild a betslip-shaped ticket from an existing ticket id
 * (`sb/api/tickets/reuse`).
 */
export function reuseTicket(ticketId: string, signal?: AbortSignal): Promise<BetslipTicket> {
  return api.post<BetslipTicket>(
    endpoints.tickets.reuse.host,
    endpoints.tickets.reuse.path,
    { Value: ticketId },
    signal,
  );
}

/** Refresh odds / availability on a reused slip (`api/betting/refresh`). */
export function refreshTicket(
  ticket: BetslipTicket,
  signal?: AbortSignal,
): Promise<BetslipTicket> {
  return api.post<BetslipTicket>(
    endpoints.betting.refresh.host,
    endpoints.betting.refresh.path,
    ticket as unknown as Record<string, unknown>,
    signal,
  );
}

/**
 * Refresh open-ticket odds / probabilities for cashout offer calc
 * (`POST api/v1/betting/cashoutlist`). Does not settle money.
 */
export function fetchCashoutList(
  tickets: OpenCashoutTicket[],
  signal?: AbortSignal,
): Promise<OpenCashoutTicket[]> {
  return api.post<OpenCashoutTicket[]>(
    endpoints.betting.cashoutList.host,
    endpoints.betting.cashoutList.path,
    { Tickets: tickets } as unknown as Record<string, unknown>,
    signal,
  );
}

/**
 * Execute full or partial cashout via Users API (`sb/api/tickets/cashoutnew`).
 * Do not call SportsAPI for settlement.
 */
export function cashoutTicket(
  request: CashoutTicketRequest,
  signal?: AbortSignal,
): Promise<CashoutTicketResponse> {
  return api.post<CashoutTicketResponse>(
    endpoints.tickets.cashout.host,
    endpoints.tickets.cashout.path,
    request as unknown as Record<string, unknown>,
    signal,
  );
}
